import { Link, Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { TravelBooking } from '../queries';
import type { BookingLegPickupView } from '../bookingLegs';
import {
  bookingLegPickupReassuranceVisible,
  bookingLegViews,
} from '../bookingLegs';
import { serviceDayLabel } from '../bookingListDays';
import { formatProviderDateTime } from '../format';
import { PickupVerificationNotice } from './PickupVerificationNotice';

interface Props {
  /**
   * Los tramos del registro AUTORITATIVO de la reserva (§21.23): la fila que
   * devuelven el hold, el confirm, el sync y las lecturas. Nunca la oferta
   * pre-hold ni el resumen congelado del formulario.
   */
  legs: TravelBooking['legs'];
  passengers: TravelBooking['passengers'];
  bookingState: TravelBooking['state'];
}

/**
 * La hora de recogida, tal como el contrato manda mostrarla: HORA LOCAL del
 * punto (cero conversiones — es la hora que el pasajero lee en su reloj), con
 * la zona IANA al lado cuando el hub la publica. Con `reconfirm` no hay hora:
 * se dice "a reconfirmar" y el día si el proveedor lo publicó.
 */
function PickupValue({
  pickup,
  showConfirmedReassurance,
}: {
  pickup: BookingLegPickupView;
  showConfirmedReassurance: boolean;
}) {
  const { t, i18n } = useTranslation();
  const dateLabel = pickup.date
    ? serviceDayLabel(pickup.date, i18n.language) ?? pickup.date
    : undefined;
  if (pickup.status === 'reconfirm') {
    // La antelación y el link son el aviso OPERATIVO del contrato: sin ellos
    // "a reconfirmar" no dice ni cuándo ni dónde. Es la MISMA información que
    // muestra la oferta — que el asesor no vea una cosa antes de reservar y
    // otra después. Sin metadatos queda el aviso genérico de siempre.
    const { minHoursBefore, url } = pickup;
    return (
      <Stack spacing={0.5}>
        {dateLabel && <Typography variant="body2" fontWeight={600}>{dateLabel}</Typography>}
        <Typography variant="body2" fontWeight={600} color="warning.dark">
          {t('travelServices.legs.pickupReconfirm')}
        </Typography>
        {!pickup.timeVerification && <Typography variant="body2">
          {minHoursBefore !== undefined
            ? t('travelServices.legs.pickupReconfirmWithin', { hours: minHoursBefore })
            : t('travelServices.legs.pickupReconfirmGeneric')}
        </Typography>}
        {showConfirmedReassurance && (
          <Typography variant="caption" color="text.secondary">
            {t('travelServices.legs.pickupReconfirmConfirmed')}
          </Typography>
        )}
        {!pickup.timeVerification && url && (
          <Link href={url} target="_blank" rel="noopener noreferrer" variant="body2">
            {t('travelServices.legs.pickupReconfirmLink')}
          </Link>
        )}
        <PickupVerificationNotice verification={pickup.timeVerification} />
      </Stack>
    );
  }
  const timeLabel = pickup.time
    ? t(
      pickup.timeZone
        ? 'travelServices.legs.localTimeWithZone'
        : 'travelServices.legs.localTime',
      { time: pickup.time, timeZone: pickup.timeZone },
    )
    : undefined;
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        {[dateLabel, timeLabel].filter(Boolean).join(' · ')}
      </Typography>
      <PickupVerificationNotice verification={pickup.timeVerification} />
    </Stack>
  );
}

/**
 * Los TRAMOS del traslado (`booking.legs`, solo transfers): por tramo, la
 * recogida con su estado, el punto de encuentro y la categoría del vehículo.
 * Data-driven y autoocultable: un booking sin tramos —otros verticales,
 * proveedor sin detalle— no dibuja nada. El punto de encuentro aparece
 * típicamente recién cuando hay operador asignado y llega con el sync.
 */
export function BookingLegsSection({ legs, passengers, bookingState }: Props) {
  const { t } = useTranslation();
  const views = bookingLegViews(legs, passengers);
  if (!views) return null;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('travelServices.legs.section')}
      </Typography>
      <Stack spacing={1.5}>
        {views.map((leg) => (
          <Box key={leg.key} border={1} borderColor="divider" borderRadius={2} p={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Typography variant="body2" fontWeight={600}>
                {t(
                  leg.direction === 'return'
                    ? 'travelServices.booking.return'
                    : 'travelServices.booking.outbound',
                )}
              </Typography>
              {/* El estado POR TRAMO, solo cuando distingue algo: la vuelta
                  cancelada de un roundtrip cancelado a medias. Para el estado
                  de la reserva manda booking.state (el chip de arriba). */}
              {leg.cancelled && (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={t('travelServices.bookingStates.cancelled')}
                />
              )}
            </Stack>
            <Stack spacing={0.75}>
              {leg.pickup && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t('travelServices.legs.pickup')}
                  </Typography>
                  <PickupValue
                    pickup={leg.pickup}
                    showConfirmedReassurance={
                      bookingLegPickupReassuranceVisible(
                        bookingState,
                        leg.cancelled,
                      )
                    }
                  />
                </Box>
              )}
              {leg.meetingPointName && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t('travelServices.legs.meetingPoint')}
                  </Typography>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {leg.meetingPointName}
                  </Typography>
                </Box>
              )}
              {leg.meetingPointDescription && (
                <Box bgcolor="grey.50" borderRadius={1} px={1.5} py={1.25}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    {t('travelServices.legs.driverInstructions')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}
                  >
                    {leg.meetingPointDescription}
                  </Typography>
                </Box>
              )}
              {leg.instructionsUnavailable && (
                <Typography variant="body2" color="warning.dark">
                  {t('travelServices.legs.instructionsUnavailable')}
                </Typography>
              )}
              {leg.vehicleCategory && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t('travelServices.legs.vehicle')}
                  </Typography>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {leg.vehicleCategory}
                  </Typography>
                </Box>
              )}
              {(['origin', 'destination'] as const).map(end => {
                const transport = leg.associatedTransports?.[end];
                if (!transport) return null;
                return <Box key={end}>
                  <Typography variant="caption" color="text.secondary" display="block">{t(`travelServices.transportDetails.${end}`)} · {t(`travelServices.transportDetails.${transport.type}`)}</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{transport.number}{transport.company ? ` · ${transport.company}` : ''}</Typography>
                  {transport.scheduledLocalTime && <Typography variant="body2">{t('travelServices.transportDetails.localTime')}: {transport.scheduledLocalTime.replace('T', ' ')}</Typography>}
                  <Typography variant="caption" color="text.secondary">{t(`travelServices.transportDetails.source.${transport.source}`)}</Typography>
                </Box>;
              })}
              {leg.associatedFlight && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('travelServices.legs.associatedFlight')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {leg.associatedFlight.number}
                  </Typography>
                  {leg.associatedFlight.scheduledLocalTime && (
                    <Typography variant="caption" color="text.secondary">
                      {t('travelServices.legs.associatedFlightTime', {
                        time: leg.associatedFlight.scheduledLocalTime.replace('T', ' '),
                      })}
                    </Typography>
                  )}
                </Box>
              )}
              {leg.freeCancellationUntil && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t('travelServices.legs.freeCancellationUntil')}
                  </Typography>
                  <Typography variant="body2">
                    {formatProviderDateTime(leg.freeCancellationUntil)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
